// Layout wiki della documentazione — estratto verbatim da DeveloperSettings.tsx.
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Plus, Trash2, Key, Shield, Eye, EyeOff, AlertTriangle, CheckCircle2, Download, FileText, AlertCircle, Server, Globe, Send, RefreshCw, Webhook, BookOpen, Code, Wrench, Puzzle, Lock, Pencil, Search, X, ChevronUp, ChevronDown, type LucideIcon } from 'lucide-react';
import {
  ALL_AVAILABLE_SCOPES,
  SCOPE_CATEGORIES,
  SCOPE_TO_EVENTS_MAP,
  WEBHOOK_EVENT_CATEGORIES as SHARED_EVENT_CATEGORIES,
  getAllowedEventsForScopes,
} from '@easyflow/shared';
import { format } from 'date-fns';
import { HubShell } from '@/components/common/HubShell';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient as api, getPublicBaseUrl } from '@/lib/api';
import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { SectionNav, type SectionNavSectionDef } from '@easyflow/ui-frontend/components/ui/section-nav';
import { useSectionNavState } from '@/hooks/useSectionNavState';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@easyflow/ui-frontend/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@easyflow/ui-frontend/components/ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import MCPToolsTab from '@/components/mcp/MCPToolsTab';
import MCPGatewayTab from '@/components/mcp/MCPGatewayTab';
import IntegrationTemplatesTab from '@/components/mcp/IntegrationTemplatesTab';
import IdentitySelector from '@/components/ai/IdentitySelector';
import { useToast } from '@/hooks/use-toast';
import { useAuroraTheme } from '@/components/aurora/useAuroraTheme';
import { aurora } from '@/components/aurora/tokens';

import { API_DOC_SECTIONS } from './primitives';

export function DocumentationWikiLayout({
  activeSection, onChangeSection, onExportSection, onExportFull, renderSection, isExporting,
}: {
  activeSection: string;
  onChangeSection: (id: string) => void;
  onExportSection: () => void;
  onExportFull: () => void;
  renderSection: () => React.ReactNode;
  isExporting: boolean;
}) {
  const { t } = useAuroraTheme();
  const activeMeta = API_DOC_SECTIONS.find(s => s.id === activeSection);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [matchCount, setMatchCount] = useState(0);
  const [matchIndex, setMatchIndex] = useState(-1);

  // Focus iniziale: appena il layout viene montato (apertura del tab
  // Documentation) portiamo il focus sulla barra di ricerca così
  // l'utente può iniziare a digitare subito.
  useEffect(() => {
    searchInputRef.current?.focus({ preventScroll: true });
  }, []);

  const filteredSections = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return API_DOC_SECTIONS;
    return API_DOC_SECTIONS.filter((s) => {
      if (s.title.toLowerCase().includes(q)) return true;
      if (s.id.toLowerCase().includes(q)) return true;
      if (s.keywords && s.keywords.some(k => k.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [search]);

  // Evidenziazione delle occorrenze del termine cercato nel canvas a destra.
  // Riavvolge eventuali <mark> precedenti, poi avvolge ogni match in un nuovo
  // <mark data-doc-hl>. Re-esegue su cambio termine o sezione attiva (con un
  // piccolo ritardo per attendere l'animazione di AnimatePresence).
  const highlightTerm = search.trim();
  useEffect(() => {
    let cancelled = false;
    const apply = () => {
      if (cancelled) return;
      const root = contentRef.current;
      if (!root) return;
      // Unwrap previous highlights
      const prev = root.querySelectorAll('mark[data-doc-hl]');
      prev.forEach((m) => {
        const parent = m.parentNode;
        if (!parent) return;
        while (m.firstChild) parent.insertBefore(m.firstChild, m);
        parent.removeChild(m);
      });
      root.normalize();

      if (highlightTerm.length < 2) {
        setMatchCount(0);
        setMatchIndex(-1);
        return;
      }

      const lower = highlightTerm.toLowerCase();
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          const p = node.parentElement;
          if (!p) return NodeFilter.FILTER_REJECT;
          const tag = p.tagName;
          if (tag === 'SCRIPT' || tag === 'STYLE') return NodeFilter.FILTER_REJECT;
          const v = node.nodeValue;
          if (!v || !v.toLowerCase().includes(lower)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      const targets: Text[] = [];
      let n: Node | null;
      while ((n = walker.nextNode())) targets.push(n as Text);

      let count = 0;
      for (const textNode of targets) {
        const text = textNode.nodeValue;
        const lowerText = text!.toLowerCase()!;
        const frag = document.createDocumentFragment();
        let idx = 0;
        let last = 0;
        while ((idx = lowerText.indexOf(lower, last)) !== -1) {
          if (idx > last) frag.appendChild(document.createTextNode(text!.slice(last, idx)!));
          const mark = document.createElement('mark');
          mark.setAttribute('data-doc-hl', '');
          mark.textContent = text!.substr(idx, lower.length)!;
          frag.appendChild(mark);
          count++;
          last = idx + lower.length;
        }
        if (last < text!.length!) frag.appendChild(document.createTextNode(text!.slice(last)!));
        textNode.parentNode?.replaceChild(frag, textNode);
      }
      setMatchCount(count);
      setMatchIndex(count > 0 ? 0 : -1);
    };
    // Quando l'utente azzera la ricerca, rimuovi subito i <mark> senza attendere
    // l'animazione: evita di lasciare evidenziazioni stantie a video.
    // Negli altri casi attende l'uscita di AnimatePresence (~180ms) prima di
    // rileggere il DOM, così la nuova sezione è già montata.
    if (highlightTerm.length < 2) {
      apply();
      return () => { cancelled = true; };
    }
    const handle = window.setTimeout(apply, 220);
    return () => { cancelled = true; window.clearTimeout(handle); };
  }, [highlightTerm, activeSection]);

  // Aggiorna lo stile del match attivo e fa auto-scroll verso di esso.
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const marks = root.querySelectorAll<HTMLElement>('mark[data-doc-hl]');
    marks.forEach((m, i) => {
      m.classList.toggle('doc-search-hl-active', i === matchIndex);
    });
    if (matchIndex >= 0 && marks[matchIndex]) {
      marks[matchIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [matchIndex, matchCount]);

  const cycleMatch = (dir: 1 | -1) => {
    if (matchCount <= 0) return;
    setMatchIndex((prev) => {
      const base = prev < 0 ? 0 : prev;
      return (base + dir + matchCount) % matchCount;
    });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (search) {
        e.preventDefault();
        setSearch('');
      }
      return;
    }
    if (e.key === 'Enter' && filteredSections.length > 0) {
      e.preventDefault();
      const isActiveInFiltered = filteredSections.some(s => s.id === activeSection);
      // Se siamo già sulla sezione corretta e ci sono match, ciclo tra loro.
      if (isActiveInFiltered && matchCount > 0) {
        cycleMatch(e.shiftKey ? -1 : 1);
        return;
      }
      const next = isActiveInFiltered ? activeSection : filteredSections[0]!.id!;
      onChangeSection(next);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (filteredSections.length === 0) return;
      e.preventDefault();
      const idx = filteredSections.findIndex(s => s.id === activeSection);
      const nextIdx = e.key === 'ArrowDown'
        ? (idx < 0 ? 0 : (idx + 1) % filteredSections.length)
        : (idx <= 0 ? filteredSections.length - 1 : idx - 1);
      onChangeSection(filteredSections[nextIdx]!.id!);
    }
  };

  return (
    <div
      className="flex flex-col rounded-xl overflow-hidden"
      style={{
        height: 'calc(100vh - 220px)',
        minHeight: 560,
        background: t.panel,
        border: `1px solid ${t.border}`,
        boxShadow: `0 1px 0 ${t.border}`,
      }}
    >
      {/* Header (non-scrollable) */}
      <div
        className="shrink-0 flex items-center justify-between gap-3 px-5 py-4"
        style={{ borderBottom: `1px solid ${t.border}`, background: t.panel }}
      >
        <div className="min-w-0">
          <h3
            className="truncate"
            style={{
              fontFamily: aurora.serif,
              fontWeight: 500,
              fontSize: 22,
              lineHeight: 1.15,
              letterSpacing: '-0.01em',
              color: t.text,
            }}
          >
            API v2 Documentation
          </h3>
          <p
            className="mt-0.5 text-[12.5px]"
            style={{ fontFamily: aurora.sans, color: t.muted }}
          >
            Complete reference for the EDGVoIP REST API
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={onExportSection}
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[12px] font-medium transition-colors"
            style={{ background: t.panel2, color: t.muted, border: `1px solid ${t.border}` }}
            onMouseEnter={(e) => { e.currentTarget.style.color = t.text; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = t.muted; }}
          >
            <Download className="w-3.5 h-3.5" />
            Export Section
          </button>
          <button
            onClick={onExportFull}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12px] font-semibold"
            style={{
              background: `linear-gradient(135deg, ${t.primary} 0%, ${t.primaryStrong} 100%)`,
              color: '#fff',
              boxShadow: `0 6px 18px -8px ${t.primary}88`,
            }}
          >
            <Download className="w-3.5 h-3.5" />
            Export Full Docs
          </button>
        </div>
      </div>

      {/* Two-column body */}
      <div className="flex-1 min-h-0 flex">
        {/* Sidebar (independent scroll) */}
        <aside
          className="shrink-0 overflow-y-auto"
          style={{
            width: 264,
            background: t.panel,
            borderRight: `1px solid ${t.border}`,
          }}
          aria-label="Documentation sections"
        >
          <div className="px-3 pt-4 pb-2 flex items-center justify-between">
            <span
              className="text-[10.5px] font-semibold uppercase tracking-[0.14em]"
              style={{ color: t.muted, fontFamily: aurora.sans }}
            >
              Reference
            </span>
            <span
              className="text-[10px] tabular-nums"
              style={{ color: t.faint, fontFamily: aurora.mono }}
            >
              {search ? `${filteredSections.length}/${API_DOC_SECTIONS.length}` : API_DOC_SECTIONS.length}
            </span>
          </div>
          <div className="px-3 pb-2">
            <div className="relative">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none"
                style={{ color: t.faint }}
              />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Cerca sezione…"
                aria-label="Cerca sezione documentazione"
                className="w-full h-8 pl-8 pr-7 rounded-md text-[12.5px] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-600 dark:focus:ring-blue-500"
                style={{
                  background: t.panel2,
                  border: `1px solid ${t.border}`,
                  color: t.text,
                  fontFamily: aurora.sans,
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = t.primary; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = t.border; }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearch(''); searchInputRef.current?.focus(); }}
                  aria-label="Pulisci ricerca"
                  title="Pulisci ricerca"
                  className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded transition-colors"
                  style={{ color: t.muted }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = t.panel; e.currentTarget.style.color = t.text; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = t.muted; }}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {highlightTerm.length >= 2 && (
              <div
                className="mt-1.5 flex items-center justify-between gap-2 px-1"
                style={{ fontFamily: aurora.sans }}
              >
                <span
                  className="text-[10.5px] tabular-nums"
                  style={{ color: matchCount > 0 ? t.muted : t.faint }}
                >
                  {matchCount > 0
                    ? `${matchIndex + 1} / ${matchCount} occorrenze`
                    : 'Nessuna occorrenza nel canvas'}
                </span>
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => cycleMatch(-1)}
                    disabled={matchCount === 0}
                    aria-label="Match precedente"
                    title="Match precedente (Shift+Enter)"
                    className="w-5 h-5 grid place-items-center rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ color: t.muted }}
                    onMouseEnter={(e) => { if (matchCount > 0) { e.currentTarget.style.background = t.panel; e.currentTarget.style.color = t.text; } }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = t.muted; }}
                  >
                    <ChevronUp className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => cycleMatch(1)}
                    disabled={matchCount === 0}
                    aria-label="Match successivo"
                    title="Match successivo (Enter)"
                    className="w-5 h-5 grid place-items-center rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ color: t.muted }}
                    onMouseEnter={(e) => { if (matchCount > 0) { e.currentTarget.style.background = t.panel; e.currentTarget.style.color = t.text; } }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = t.muted; }}
                  >
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </div>
          <nav className="px-2 pb-4 space-y-0.5">
            {filteredSections.length === 0 && (
              <div
                className="px-2.5 py-3 text-[12px] text-center"
                style={{ color: t.faint, fontFamily: aurora.sans }}
              >
                Nessuna sezione trovata
              </div>
            )}
            {filteredSections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => onChangeSection(section.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-colors"
                  style={{
                    background: isActive ? `${t.primary}1A` : 'transparent',
                    color: isActive ? t.text : t.muted,
                    boxShadow: isActive ? `inset 2px 0 0 0 ${t.primary}` : 'none',
                    fontFamily: aurora.sans,
                    fontSize: 12.5,
                    fontWeight: isActive ? 600 : 500,
                  }}
                  onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.background = t.panel2; e.currentTarget.style.color = t.text; } }}
                  onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = t.muted; } }}
                >
                  <Icon
                    className="w-3.5 h-3.5 shrink-0"
                    style={{ color: isActive ? t.primary : t.faint }}
                  />
                  <span className="truncate">{section.title}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Canvas (independent scroll) */}
        <main
          className="flex-1 min-w-0 overflow-y-auto"
          style={{
            background: t.bg,
          }}
        >
          <div
            id="doc-content"
            ref={contentRef}
            className="mx-auto px-6 md:px-10 py-8 max-w-[920px]"
            style={{
              fontFamily: aurora.sans,
              color: t.text,
            }}
          >
            {(() => {
              const sectionBody = (
                <>
                  {activeMeta && (
                    <div className="mb-5 flex items-center gap-2">
                      <span
                        className="text-[10.5px] font-semibold uppercase tracking-[0.16em]"
                        style={{ color: t.muted }}
                      >
                        Section
                      </span>
                      <span
                        className="text-[10.5px] font-semibold uppercase tracking-[0.16em]"
                        style={{ color: t.faint }}
                      >
                        ·
                      </span>
                      <span
                        className="text-[10.5px] font-semibold uppercase tracking-[0.16em]"
                        style={{ color: t.primary, fontFamily: aurora.mono }}
                      >
                        {activeMeta.title}
                      </span>
                    </div>
                  )}
                  <div className="aurora-doc-canvas">
                    {renderSection()}
                  </div>
                </>
              );

              // While exporting, render synchronously without
              // AnimatePresence so each section is in the DOM the moment
              // React commits — keeps `downloadFullMarkdown` deterministic.
              if (isExporting) {
                return <div key={activeSection}>{sectionBody}</div>;
              }

              return (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeSection}
                    initial={aurora.fadeUp.initial}
                    animate={aurora.fadeUp.animate}
                    exit={{ opacity: 0, y: -8, transition: { duration: 0.18 } }}
                    transition={aurora.fadeUp.transition}
                  >
                    {sectionBody}
                  </motion.div>
                </AnimatePresence>
              );
            })()}
          </div>
        </main>
      </div>
    </div>
  );
}

